import React, { useEffect, useState } from 'react';
import { Box, Checkbox, Stack, VStack, Heading, Text, HStack } from '@chakra-ui/react';
import { ToggleAtom } from '../../atoms/CustomToggle';
import { FaqArticle } from '../../organisms/PopularArticles';

interface FAQ {
  publish_id: string;
  faq_title: string;
}

interface Section {
  section_id: string;
  section_name: string;
  faq_data: FAQ[];
}

interface FAQSelectionOrganismProps {
  sectionsData: Section[] | null;
  onSelectionChange: (selectedFaqs: { id: string; title: string; category: string }[]) => void;
  configs: {
    showPopularArticles: boolean;
  };
  data: {
    popularArticles: FaqArticle[];
  };
  onConfigChange: (key: string, value: any) => void;
  onValidityChange?: (isValid: boolean) => void;
}

export const PopularArticlesSettingsMolecule: React.FC<FAQSelectionOrganismProps> = ({
  sectionsData,
  onSelectionChange,
  configs,
  data,
  onConfigChange,
  onValidityChange,
}) => {
  const [selectedFaqIds, setSelectedFaqIds] = useState<string[]>(
    data?.popularArticles?.map(article => String(article.id)) || [],
  );

  const [selectedFaqs, setSelectedFaqs] = useState<
    { id: string; title: string; category: string }[]
  >(
    data?.popularArticles?.map(article => ({
      id: String(article.id),
      title: article.title,
      category: article.category,
    })) || [],
  );

  const [showPopularArticles, setShowPopularArticles] = useState(configs.showPopularArticles);
  useEffect(() => {
    if (selectedFaqIds.length) {
      onValidityChange?.(selectedFaqIds.length === 3);
    }
  }, []);
  useEffect(() => {
    const isValid = selectedFaqIds.length === 3;
    onValidityChange?.(isValid);
  }, []);

  useEffect(() => {
    onValidityChange?.(selectedFaqIds.length === 3);
  }, [selectedFaqIds]);

  const getFaqDetails = (faqId: string) => {
    if (!sectionsData) return null;

    const foundFaq = sectionsData
      .flatMap(section =>
        section.faq_data.map(faq => ({
          id: faq.publish_id,
          title: faq.faq_title,
          category: section.section_name,
        })),
      )
      .find(faq => faq.id === faqId);

    return foundFaq || null;
  };

  const handleCheckboxChange = (faqId: string, isChecked: boolean) => {
    const foundFaq = getFaqDetails(faqId);
    if (!foundFaq) return;

    let newSelectedFaqs: { id: string; title: string; category: string }[] = [];
    let newSelectedFaqIds: string[] = [];

    if (isChecked) {
      if (selectedFaqs.length >= 3 || selectedFaqIds.includes(faqId)) return;

      newSelectedFaqs = [...selectedFaqs, foundFaq];
      newSelectedFaqIds = [...selectedFaqIds, faqId];
    } else {
      newSelectedFaqs = selectedFaqs.filter(f => f.id !== faqId);
      newSelectedFaqIds = selectedFaqIds.filter(id => id !== faqId);
    }

    setSelectedFaqs(newSelectedFaqs);
    setSelectedFaqIds(newSelectedFaqIds);

    const paddedTop3ToSend = [
      ...newSelectedFaqs,
      ...Array(Math.max(0, 3 - newSelectedFaqs.length))
        .fill(null)
        .map(() => ({
          id: '',
          title: '',
          category: '',
        })),
    ].slice(0, 3);

    onSelectionChange(paddedTop3ToSend);
  };

  return (
    <Box
      border="1px solid #e2e8f0"
      borderRadius="md"
      display="flex"
      flexDirection="column"
      height="80vh"
    >
      {/* Toggle */}
      <HStack
        p={4}
        borderBottom="1px solid #e2e8f0"
        justifyContent="space-between"
        alignItems="center"
      >
        <ToggleAtom
          justifyContent="space-between"
          label="Show Popular Articles"
          value={showPopularArticles}
          onChange={value => {
            setShowPopularArticles(value);
            onConfigChange('showPopularArticles', value);
          }}
        />
      </HStack>

      {/* Scrollable FAQ List */}
      <Box flex="1" overflowY="auto" p={4}>
        <Stack spacing={6}>
          {sectionsData?.map(section => (
            <Box key={section.section_id} borderBottom="1px solid #e2e8f0" pb={4}>
              <Heading size="sm" mb={2}>
                {section.section_name}
              </Heading>
              <VStack align="start" spacing={2}>
                {section.faq_data.map(faq => (
                  <Checkbox
                    key={faq.publish_id}
                    value={faq.publish_id}
                    isChecked={selectedFaqIds.includes(faq.publish_id)}
                    isDisabled={
                      !selectedFaqIds.includes(faq.publish_id) && selectedFaqIds.length >= 3
                    }
                    onChange={e => handleCheckboxChange(faq.publish_id, e.target.checked)}
                  >
                    {faq.faq_title}
                  </Checkbox>
                ))}
              </VStack>
            </Box>
          ))}
        </Stack>
      </Box>
      <Box borderTop="1px solid #e2e8f0" bg="white" p={3} position="sticky" bottom="0" zIndex="10">
        <Text fontWeight="medium">Selected FAQs: {selectedFaqIds.length} / 3</Text>
        {selectedFaqIds.length < 3 && (
          <Box mt={2} p={3} bg="red.100" border="1px solid" borderColor="red.300" borderRadius="md">
            <Text fontWeight="bold" fontSize="md" color="red.700" textAlign="center">
              ⚠️ Please select exactly 3 FAQs to proceed.
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};
